<template>
    <div>
        <slot
            :setFilter="setFilter"
            :updateFilter="updateFilter"
            :query="query"
        />

        <button
            @click="reset"
            v-if="activeFilters.length || Object.keys(activeFilterItems).length"
            class="btn btn-sm btn-gray mt-3"
        >
            Reset
        </button>
    </div>
</template>

<script>
    import { diff } from 'deep-object-diff';
    import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';

    import sleep from '../../../helpers/sleep';

    export default {
        name: 'FilterBase',
        props: {
            baseUrl: {
                type: String,
                default: window.location.pathname,
            },
            filterConfig: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                query: {},
                oldValues: null,
            };
        },
        created() {
            this.setAllFilters(this.filterConfig);
        },
        computed: {
            ...mapState('filters', {
                filters: 'filters',
                originalQuery: 'query',
                defaultValues: 'defaultValues',
            }),

            ...mapGetters('filters', [
                'activeFilters',
            ]),

            activeFilterItems() {
                const difference = diff(this.defaultValues, this.query);
                const differenceFiltered = Object.keys(difference)
                    .filter(item => typeof difference[item] !== 'string')
                    .reduce((obj, key) => {
                        obj[key] = difference[key];
                        return obj;
                    }, {});

                return differenceFiltered;
            },
        },
        mounted() {
            eventHub.$on('pageChange', (page) => {
                this.query.page = page;
                this.updateQuery(this.query);
            });

            eventHub.$on('pageLengthChange', (length) => {
                this.query.length = length;
                this.updateQuery(this.query);
            });

            this.cloneQuery();
        },
        methods: {
            ...mapMutations('filters', [
                'setAllFilters',
                'updateQuery',
            ]),

            ...mapActions('filters', [
                'resetFilters',
            ]),

            cloneQuery() {
                this.query = JSON.parse(JSON.stringify(this.originalQuery));
            },

            async updateFilter() {
                if (this.query.search && this.query.search.length < 3) {
                    return false;
                }

                this.query.page = 0;

                await sleep(25);
                this.updateQuery(this.query);
            },

            updateDatatableFilter(column, value) {
                eventHub.$emit('updateFilter', { column, value });
            },

            reset() {
                this.resetFilters();
                this.cloneQuery();
                window.history.replaceState({}, '', window.location.pathname);
            },

            setFilter(filter, queryKey) {
                if (queryKey === 'date-range') {
                    this.query['date_start'] = filter.start;
                    this.query['date_end'] = filter.end;
                } else {
                    if (Array.isArray(filter)) {
                        this.query[queryKey] = filter.map(({ id }) => ({ id }));
                    } else {
                        this.query[queryKey] = filter;
                    }
                }

                this.updateFilter();
            },
        },
        watch: {
            query: {
                async handler(values) {
                    const changes = diff(this.oldValues, values);

                    Object.keys(changes).forEach((key) => {
                        const filtersKey = this.filters[key];
                        let setValues = changes[key];

                        if (typeof filtersKey === 'undefined') {
                            return;
                        }

                        if (typeof filtersKey.value === 'object') {
                            setValues = this.query[key];

                            // If value contains array, then get ID's from objects
                            if (typeof setValues === 'object' && setValues.some(item => typeof item === 'object')) {
                                setValues = setValues.map(item => item.id);
                            }
                        }

                        if (filtersKey && filtersKey.dataTableId) {
                            this.updateDatatableFilter(filtersKey.dataTableId, setValues);
                        }
                    });

                    this.oldValues = JSON.parse(JSON.stringify(values));
                },
                deep: true,
            },
        },
    };
</script>
